import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [bookingType, setBookingType] = useState('enquiry'); // 'enquiry' or 'payment'
    
    // Catering-specific state
    const [eventDetails, setEventDetails] = useState({
        date: null,
        time: null,
        venue: '',
        occasion: '',
        guestCount: 0,
        dietaryPreferences: {
            vegetarian: 0,
            nonVegetarian: 0,
            vegan: 0,
            specialDietary: []
        },
        specialInstructions: ''
    });
    
    const [serviceRequirements, setServiceRequirements] = useState({
        setup: false,
        serving: false,
        cleanup: false,
        staffCount: 0,
        equipment: []
    });

    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('catering_cart');
        const cartExpiry = localStorage.getItem('catering_cart_expiry');
        
        if (savedCart && cartExpiry) {
            try {
                const expiryTime = parseInt(cartExpiry);
                const currentTime = Date.now();
                
                // Check if cart has expired (24 hours)
                if (currentTime > expiryTime) {
                    localStorage.removeItem('catering_cart');
                    localStorage.removeItem('catering_cart_expiry');
                    console.log('Cart expired and cleared');
                } else {
                    const parsedCart = JSON.parse(savedCart);
                    // Validate cart items structure
                    if (Array.isArray(parsedCart)) {
                        setCartItems(parsedCart);
                    }
                }
            } catch (error) {
                console.error('Failed to load cart from localStorage:', error);
                localStorage.removeItem('catering_cart');
                localStorage.removeItem('catering_cart_expiry');
            }
        }
    }, []);

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (cartItems.length > 0) {
            localStorage.setItem('catering_cart', JSON.stringify(cartItems));
            // Set expiry to 24 hours from now
            const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
            localStorage.setItem('catering_cart_expiry', expiryTime.toString());
        } else {
            localStorage.removeItem('catering_cart');
            localStorage.removeItem('catering_cart_expiry');
        }
    }, [cartItems]);

    const addToCart = (menuItem, quantity = 1, customizations = {}) => {
        const existingItemIndex = cartItems.findIndex(
            item => item.id === menuItem.id && 
            JSON.stringify(item.customizations) === JSON.stringify(customizations)
        );

        if (existingItemIndex > -1) {
            // Update existing item quantity
            const updatedItems = [...cartItems];
            updatedItems[existingItemIndex].quantity += quantity;
            setCartItems(updatedItems);
        } else {
            // Add new item
            const newItem = {
                id: menuItem.id,
                name: menuItem.name,
                description: menuItem.description,
                price: menuItem.price,
                image: menuItem.image,
                vendor_id: menuItem.vendor_id,
                vendor_name: menuItem.vendor?.business_name || 'Unknown Vendor',
                quantity,
                customizations,
                addedAt: new Date().toISOString()
            };
            setCartItems([...cartItems, newItem]);
        }
    };

    // Add catering package to cart
    const addCateringPackage = (packageData) => {
        const newPackage = {
            id: `package_${Date.now()}`,
            type: 'catering_package',
            name: packageData.name,
            description: packageData.description,
            price: packageData.price,
            vendor_id: packageData.vendor_id,
            vendor_name: packageData.vendor_name,
            guestCount: packageData.guestCount,
            menuItems: packageData.menuItems,
            serviceType: packageData.serviceType,
            addedAt: new Date().toISOString()
        };
        setCartItems([...cartItems, newPackage]);
    };

    // Update event details
    const updateEventDetails = (details) => {
        setEventDetails(prev => ({ ...prev, ...details }));
    };

    // Update service requirements
    const updateServiceRequirements = (requirements) => {
        setServiceRequirements(prev => ({ ...prev, ...requirements }));
    };

    const removeFromCart = (itemId, customizations = {}) => {
        setCartItems(cartItems.filter(item => 
            !(item.id === itemId && 
            JSON.stringify(item.customizations) === JSON.stringify(customizations))
        ));
    };

    const updateQuantity = (itemId, quantity, customizations = {}) => {
        if (quantity <= 0) {
            removeFromCart(itemId, customizations);
            return;
        }

        const updatedItems = cartItems.map(item => {
            if (item.id === itemId && 
                JSON.stringify(item.customizations) === JSON.stringify(customizations)) {
                return { ...item, quantity };
            }
            return item;
        });
        setCartItems(updatedItems);
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const getCartTotal = () => {
        return cartItems.reduce((total, item) => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseInt(item.quantity) || 0;
            return total + (price * quantity);
        }, 0);
    };

    const getCartItemCount = () => {
        return cartItems.reduce((total, item) => total + item.quantity, 0);
    };

    const getCartItemsByVendor = () => {
        const vendorGroups = {};
        cartItems.forEach(item => {
            if (!vendorGroups[item.vendor_id]) {
                vendorGroups[item.vendor_id] = {
                    vendor_id: item.vendor_id,
                    vendor_name: item.vendor_name,
                    items: []
                };
            }
            vendorGroups[item.vendor_id].items.push(item);
        });
        return Object.values(vendorGroups);
    };

    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);

    const value = {
        cartItems,
        isCartOpen,
        bookingType,
        setBookingType,
        eventDetails,
        serviceRequirements,
        addToCart,
        addCateringPackage,
        removeFromCart,
        updateQuantity,
        updateEventDetails,
        updateServiceRequirements,
        clearCart,
        getCartTotal,
        getCartItemCount,
        getCartItemsByVendor,
        openCart,
        closeCart
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
